import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 24, 2026";

export const NODEMAVEN_URL = "https://nodemaven.com";
export const NODEMAVEN_PRICING_URL = "https://nodemaven.com/pricing/";
export const NODEMAVEN_RESIDENTIAL_URL = "https://nodemaven.com/proxies/residential-proxies/";
export const NODEMAVEN_MOBILE_URL = "https://nodemaven.com/proxies/mobile-proxies/";

export type ProxyProduct = "mobile" | "residential";

export interface NodemavenPlan {
  name: string;
  gb: number;
  monthlyPrice: number;
  perGb: number;
}

// NodeMaven pools residential and mobile traffic into the same monthly plan and
// the same volume-tiered per-GB rate — there is no separate mobile vs.
// residential price. Figures pulled directly from NodeMaven's "Billed monthly"
// pricing slider (all 9 published steps, discounted price shown by default).
export const NODEMAVEN_PLANS: NodemavenPlan[] = [
  { name: "2 GB", gb: 2, monthlyPrice: 8.5, perGb: 4.25 },
  { name: "8 GB", gb: 8, monthlyPrice: 32, perGb: 4.0 },
  { name: "20 GB", gb: 20, monthlyPrice: 75, perGb: 3.75 },
  { name: "40 GB", gb: 40, monthlyPrice: 138, perGb: 3.45 },
  { name: "55 GB", gb: 55, monthlyPrice: 175, perGb: 3.18 },
  { name: "100 GB", gb: 100, monthlyPrice: 300, perGb: 3.0 },
  { name: "200 GB", gb: 200, monthlyPrice: 570, perGb: 2.85 },
  { name: "450 GB", gb: 450, monthlyPrice: 1170, perGb: 2.6 },
  { name: "1,000 GB", gb: 1000, monthlyPrice: 2200, perGb: 2.2 },
];

// NodeMaven's pay-as-you-go rate at the same 9 slider steps — a fixed upfront
// purchase rather than a recurring monthly plan, priced higher than the
// equivalent monthly-plan tier at every step.
export const NODEMAVEN_PAYG_PLANS: NodemavenPlan[] = [
  { name: "2 GB", gb: 2, monthlyPrice: 10, perGb: 5.0 },
  { name: "8 GB", gb: 8, monthlyPrice: 39, perGb: 4.88 },
  { name: "20 GB", gb: 20, monthlyPrice: 90, perGb: 4.5 },
  { name: "40 GB", gb: 40, monthlyPrice: 164, perGb: 4.1 },
  { name: "55 GB", gb: 55, monthlyPrice: 205, perGb: 3.73 },
  { name: "100 GB", gb: 100, monthlyPrice: 345, perGb: 3.45 },
  { name: "200 GB", gb: 200, monthlyPrice: 650, perGb: 3.25 },
  { name: "450 GB", gb: 450, monthlyPrice: 1315, perGb: 2.92 },
  { name: "1,000 GB", gb: 1000, monthlyPrice: 2450, perGb: 2.45 },
];

// NodeMaven's own trial offer — shown as competitor context in the markdown
// comparison, not used by the bandwidth slider.
export const NODEMAVEN_TRIAL_PRICE = 3.5;
export const NODEMAVEN_TRIAL_GB = 0.75;

// ISP proxies: list price is $4.99/IP for a 30-day term; a bulk discount code
// unlocks automatically at 3+ proxies, bringing 30-day pricing down to
// $2.99/IP (confirmed at a 10-proxy quantity) and 90-day terms down to a
// lower monthly-equivalent rate still (confirmed: 10 proxies/90 days = $77.90
// total, ~$2.60/proxy/month).
export const NODEMAVEN_ISP_LIST_PRICE_PER_IP = 4.99;
export const NODEMAVEN_ISP_BULK_PRICE_PER_IP = 2.99;

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface NodemavenCost {
  cost: number;
  planLabel: string;
  extrapolated: boolean;
}

// NodeMaven prices the same combined residential+mobile pool regardless of
// which product you're comparing against on the Proxidize side, so this
// intentionally ignores the `product` argument — it's accepted only so the
// calculator can call it the same way it calls the Proxidize tier lookup.
// Above NodeMaven's largest published plan (1,000 GB), we apply that plan's
// rate — the cheapest rate they publish, so this doesn't overstate their cost.
export function nodemavenMonthlyCost(gb: number): NodemavenCost {
  const plan = NODEMAVEN_PLANS.find((p) => p.gb >= gb);
  if (plan)
    return {
      cost: plan.monthlyPrice,
      planLabel: `${plan.name} plan`,
      extrapolated: false,
    };
  const top = NODEMAVEN_PLANS[NODEMAVEN_PLANS.length - 1];
  const cost = Math.round(gb * top.perGb * 100) / 100;
  return { cost, planLabel: `${top.name}+ tier rate`, extrapolated: true };
}
